import type { MenuItem } from "@sytofood/shared";

// Client-side health score, intentionally NOT from the LLM (would hallucinate
// and would break AI Gateway caching by mixing user state into the prompt).
// Composite of: how well item fits remaining kcal budget + protein density +
// AI's own confidence on the macro estimate.

type Remaining = {
  kcal: number | null;
  proteinG: number | null;
};

// 1.0 when item.kcal exactly fits remaining budget; falls off as it goes
// further below; drops fast when it overshoots. Returns 0.6 if budget unknown
// (so we can still rank by other signals).
function kcalFitScore(itemKcal: number, remainingKcal: number | null): number {
  if (remainingKcal === null || remainingKcal === 0) return 0.6;
  const ratio = itemKcal / remainingKcal;
  if (ratio <= 1) return Math.max(0.3, ratio); // 30% of budget → 0.3, 100% → 1.0
  // Overshoot: penalize hard. 1.5× budget → 0.25, 2× → 0.0
  return Math.max(0, 1 - (ratio - 1) * 1.5);
}

// Protein density: g protein per 100 kcal. ≥10 is excellent (lean meat / fish),
// 5-10 is decent, <5 is poor (pasta, dessert).
function proteinDensityScore(item: MenuItem): number {
  if (item.estimatedProteinG === null || item.estimatedKcal === 0) return 0.4;
  const per100 = (item.estimatedProteinG / item.estimatedKcal) * 100;
  if (per100 >= 10) return 1;
  if (per100 >= 5) return 0.7;
  return 0.4;
}

export function scoreMenuItem(item: MenuItem, remaining: Remaining): number {
  const kcalFit = kcalFitScore(item.estimatedKcal, remaining.kcal);
  const proteinScore = proteinDensityScore(item);
  // Slight penalty when AI itself is unsure (egzotyczna nazwa, słabo widoczne).
  return 0.55 * kcalFit + 0.3 * proteinScore + 0.15 * item.confidence;
}
